import { useState } from 'react'
import type { ReactElement } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { storeCtaHref } from './storeCtaHref'
import { mapCtaSlot } from './mapCtaSlot'
import { reportStoreOutboundTapped } from '../analytics/storeOutboundEvents'
import { realMetroAnalyticsReporter } from '../analytics/webAnalyticsReporter'
import type { MetroAnalyticsReporter } from '../interaction/metroAnalyticsReporter'
import styles from './MapAppCta.module.css'

/**
 * The map surface's store CTA — the one a person can actually tap.
 *
 * Route, station and line pages do carry store badges, but only in the
 * prerendered `<main>`, and the map clips that away with
 * `overflow: hidden` the moment the SPA mounts (see `src/index.css`).
 * Those badges are read by crawlers and by nobody else. What was left
 * for people was Safari's Smart App Banner on iOS and
 * {@link AndroidInstallBanner} on Android — nothing at all on desktop,
 * and nothing anywhere once the banner is dismissed. `web-home-cta` had
 * been reserved in the slot registry since TASK-644 and used by no link
 * (TASK-814).
 *
 * The slot follows the page ({@link mapCtaSlot}) so installs stay
 * comparable per surface rather than collapsing into one column. On an
 * iPhone the href is the city's `/go/site-<surface>` tracking link, on a
 * desktop the direct App Store link ({@link storeCtaHref}, TASK-1170);
 * `rel="nofollow"` keeps crawlers out of the click counts.
 *
 * Not shown on Android: {@link AndroidInstallBanner} already occupies
 * that role there, and two competing prompts on one screen is worse than
 * one. iOS keeps the capsule — Safari's banner is dismissible, absent in
 * in-app browsers, and gone for good once dismissed, so it cannot be
 * relied on as the platform's only prompt.
 *
 * Dismissal persists in `localStorage`, fail-safe both ways: storage
 * that throws (private mode, disabled cookies) never breaks the map —
 * the capsule simply reappears next visit.
 */

/** `localStorage` key marking the map CTA dismissed on this browser. */
export const MAP_APP_CTA_DISMISSED_KEY = 'metro.mapAppCta.dismissed'

function isDismissed(): boolean {
  try {
    return window.localStorage.getItem(MAP_APP_CTA_DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}

function persistDismissed(): void {
  try {
    window.localStorage.setItem(MAP_APP_CTA_DISMISSED_KEY, '1')
  } catch {
    // Storage unavailable — the dismissal lives for this page only.
  }
}

export interface MapAppCtaProps {
  /**
   * Analytics sink. Optional — defaults to
   * {@link realMetroAnalyticsReporter} so the outbound tap forwards to
   * GA4 + Amplitude in production. Tests inject a spy.
   */
  readonly reporter?: MetroAnalyticsReporter
}

export function MapAppCta({
  reporter = realMetroAnalyticsReporter,
}: MapAppCtaProps = {}): ReactElement | null {
  const { t } = useTranslation('common')
  const { pathname } = useLocation()
  const [dismissed, setDismissed] = useState(isDismissed)

  if (dismissed) return null
  if (/android/i.test(navigator.userAgent)) return null

  const slot = mapCtaSlot(pathname)
  const href = storeCtaHref(navigator.userAgent, 'app_store', slot)

  return (
    <div className={styles.wrap}>
      <a
        className={styles.cta}
        href={href}
        target="_blank"
        rel="nofollow noreferrer"
        onClick={() =>
          // Always the App Store: Android returns early above, so this
          // link is never the Play one.
          reportStoreOutboundTapped(reporter, { store: 'app_store', slot })
        }
      >
        <img src="/apple-touch-icon-v3.png" alt="" className={styles.icon} />
        <span className={styles.label}>{t('install_banner.cta')}</span>
      </a>
      <button
        type="button"
        className={styles.dismiss}
        aria-label={t('install_banner.dismiss')}
        onClick={() => {
          persistDismissed()
          setDismissed(true)
        }}
      >
        {'×'}
      </button>
    </div>
  )
}
